import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search,
    User,
    Phone,
    Car,
    Calendar,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Loader2,
    Zap,
} from 'lucide-react';
import {
    searchRecords,
    fetchRecordDetail,
    clearAll,
    selectList,
    selectDetailById,
} from '../store/slices/vehicleServiceRecordsSlice';
import VehicleServiceRecordDetail from '../components/VehicleServiceRecordDetail';
import Layout from './Layout';

/**
 * Vehicle Service Records — paginated search page.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ Filter bar:                                                        │
 *   │   • Name input   • Rego input   • Mobile input                     │
 *   │   • Search button (disabled until ≥1 of name/rego/mobile filled)   │
 *   └────────────────────────────────────────────────────────────────────┘
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ Paginated table — one row per service event:                       │
 *   │   Date | Customer | Mobile | Rego | (chevron)                      │
 *   │   ▼ Click row → inline drawer with full VSR detail                  │
 *   └────────────────────────────────────────────────────────────────────┘
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ Footer: total count + page navigation                              │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * No auto-search on type — the Search button is the only trigger. The search
 * spans the organisation's whole history; the table stays empty until the user
 * fills a text filter and clicks Search.
 */

const PAGE_SIZE = 20;

// ============================================
// Component
// ============================================

const VehicleServiceRecordsPage = () => {
    const dispatch = useDispatch();
    const list = useSelector(selectList);
    const detailById = useSelector(selectDetailById);

    // ── Filter state ────────────────────────────────────────────────────
    const [name, setName] = useState('');
    const [rego, setRego] = useState('');
    const [mobile, setMobile] = useState('');

    const [expandedId, setExpandedId] = useState(null);

    // Reset cached state on unmount so a return visit shows a clean slate.
    useEffect(() => () => dispatch(clearAll()), [dispatch]);

    // ── Validation ──────────────────────────────────────────────────────
    // One text filter is the only requirement — it is what bounds the search
    // now that there is no date range.
    const canSearch = Boolean(name.trim() || rego.trim() || mobile.trim());

    // ── Search handlers ─────────────────────────────────────────────────
    const runSearch = (page = 0) => {
        if (!canSearch) return;
        setExpandedId(null);
        dispatch(searchRecords({
            rego, name, mobile,
            page,
            size: PAGE_SIZE,
        }));
    };

    const handleSearchClick = () => runSearch(0);

    const handlePrevPage = () => {
        if (list.page > 0) runSearch(list.page - 1);
    };

    const handleNextPage = () => {
        if (list.page < list.totalPages - 1) runSearch(list.page + 1);
    };

    // ── Drawer expand/collapse ──────────────────────────────────────────
    const toggleExpand = (recordId) => {
        if (expandedId === recordId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(recordId);
        if (!detailById[recordId]?.data) {
            dispatch(fetchRecordDetail(recordId));
        }
    };

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <Layout>
            <div className="p-6">
                <header className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Vehicle Service Records</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Search the durable journal of completed service events.
                    </p>
                </header>

                {/* ── Filter bar ───────────────────────────────────────────── */}
                <section className="bg-white border border-gray-200 rounded-lg p-3 mb-6">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="w-48">
                            <FilterInput
                                icon={<User className="w-4 h-4" />}
                                placeholder="Customer name"
                                value={name}
                                onChange={setName}
                                onEnter={handleSearchClick}
                            />
                        </div>
                        <div className="w-36">
                            <FilterInput
                                icon={<Car className="w-4 h-4" />}
                                placeholder="Rego"
                                value={rego}
                                onChange={(v) => setRego(v.toUpperCase())}
                                onEnter={handleSearchClick}
                                fast
                            />
                        </div>
                        <div className="w-40">
                            <FilterInput
                                icon={<Phone className="w-4 h-4" />}
                                placeholder="Mobile"
                                value={mobile}
                                onChange={setMobile}
                                onEnter={handleSearchClick}
                            />
                        </div>

                        <button
                            onClick={handleSearchClick}
                            disabled={!canSearch || list.loading}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                canSearch && !list.loading
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {list.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            Search
                        </button>
                    </div>

                    {/* Inline help */}
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                        {canSearch
                            ? 'Searching all service history.'
                            : 'Enter a name, rego or mobile to enable search.'}
                        <span className="flex items-center gap-1 text-gray-400">
                            <Zap className="w-3 h-3 fill-amber-400 stroke-amber-500" />
                            Rego is the fastest way to find a record.
                        </span>
                    </div>
                </section>

                {/* ── Results ──────────────────────────────────────────────── */}
                <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {list.error ? (
                        <div className="p-6 text-sm text-red-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {list.error}
                        </div>
                    ) : list.loading && list.records.length === 0 ? (
                        <div className="p-12 flex items-center justify-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Loading...
                        </div>
                    ) : list.records.length === 0 ? (
                        <EmptyState searched={list.appliedFilters !== null} />
                    ) : (
                        <ResultsTable
                            records={list.records}
                            expandedId={expandedId}
                            onToggle={toggleExpand}
                            detailById={detailById}
                        />
                    )}

                    {list.totalElements > 0 && (
                        <Pagination
                            page={list.page}
                            totalPages={list.totalPages}
                            totalElements={list.totalElements}
                            size={list.size}
                            onPrev={handlePrevPage}
                            onNext={handleNextPage}
                            loading={list.loading}
                        />
                    )}
                </section>
            </div>
        </Layout>
    );
};

// ============================================
// Sub-components
// ============================================

/**
 * One filter field. {@code fast} adds a lightning badge on the right — set on
 * rego, which is the quickest of the three to search: a rego is short, unique to
 * one vehicle, and backed by its own org-prefixed index, so it narrows to a
 * handful of records where a partial name can match hundreds.
 */
const FilterInput = ({ icon, placeholder, value, onChange, onEnter, fast = false }) => (
    <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</div>
        <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
            className={`w-full pl-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                fast ? 'pr-8' : 'pr-3'
            }`}
        />
        {fast && (
            <span
                title="Fastest search — a rego matches one vehicle exactly"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-500 pointer-events-none"
            >
                <Zap className="w-3.5 h-3.5 fill-amber-400 stroke-amber-500" />
            </span>
        )}
    </div>
);

const EmptyState = ({ searched }) => (
    <div className="p-12 text-center">
        <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
            {searched
                ? 'No service records match these filters.'
                : 'Enter a search and click Search to begin.'}
        </p>
    </div>
);

const ResultsTable = ({ records, expandedId, onToggle, detailById }) => (
    <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
            <Th>Date</Th>
            <Th>Customer</Th>
            <Th>Mobile</Th>
            <Th>Rego</Th>
            <Th className="w-12 text-right" />
        </tr>
        </thead>
        <tbody>
        {records.map((r) => {
            const expanded = expandedId === r.id;
            const detail = detailById[r.id];
            return (
                <React.Fragment key={r.id}>
                    <tr
                        onClick={() => onToggle(r.id)}
                        className={`cursor-pointer border-b border-gray-100 transition-colors ${
                            expanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                    >
                        <Td>{formatDate(r.serviceCompletionDate)}</Td>
                        <Td className="font-medium text-gray-900">{r.customerName}</Td>
                        <Td>{r.customerMobile || '—'}</Td>
                        <Td className="font-mono text-gray-700">{r.rego}</Td>
                        <Td className="text-right text-gray-400">
                            {expanded
                                ? <ChevronUp className="w-4 h-4 inline" />
                                : <ChevronDown className="w-4 h-4 inline" />}
                        </Td>
                    </tr>
                    {expanded && (
                        <tr className="bg-gray-50">
                            <td colSpan={5} className="p-4 border-b border-gray-200">
                                <DrawerContent detail={detail} />
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            );
        })}
        </tbody>
    </table>
);

const Th = ({ children, className = '' }) => (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
        {children}
    </th>
);

const Td = ({ children, className = '' }) => (
    <td className={`px-4 py-3 text-gray-700 ${className}`}>{children}</td>
);

const DrawerContent = ({ detail }) => {
    if (!detail) return null;
    if (detail.loading) {
        return (
            <div className="flex items-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading service history…
            </div>
        );
    }
    if (detail.error) {
        return (
            <div className="flex items-center text-sm text-red-600">
                <AlertCircle className="w-4 h-4 mr-2" /> {detail.error}
            </div>
        );
    }
    if (!detail.data) return null;
    return <VehicleServiceRecordDetail record={detail.data} />;
};

const Pagination = ({ page, totalPages, totalElements, size, onPrev, onNext, loading }) => {
    const startRow = page * size + 1;
    const endRow = Math.min((page + 1) * size, totalElements);
    return (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm">
            <div className="text-gray-600">
                {startRow}–{endRow} of {totalElements}
            </div>
            <div className="flex items-center gap-2">
                <button
                    onClick={onPrev}
                    disabled={page === 0 || loading}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-gray-600">
                    Page {page + 1} of {totalPages}
                </span>
                <button
                    onClick={onNext}
                    disabled={page >= totalPages - 1 || loading}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// ============================================
// Helpers
// ============================================

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default VehicleServiceRecordsPage;